import { z } from 'zod'

/**
 * Public share links for LIST projects (personal spaces only — the
 * cross-family camping list / gift-claimable wishlist). Deliberately tiny
 * surface: a guest with the link can view, optionally check items off, and
 * optionally claim an item with a typed display name. No guest accounts, no
 * adding items, no comments. Gated by the LIST_SHARING flag on the authed
 * side; the public side re-validates link liveness + personal org + list
 * kind on every request.
 */

export const ListShareLinkSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  projectId: z.string().uuid(),
  token: z.string(),
  allowCheckOff: z.boolean(),
  allowClaim: z.boolean(),
  expiresAt: z.coerce.date().nullable().optional(),
  revokedAt: z.coerce.date().nullable().optional(),
  createdById: z.string().uuid().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})
export type ListShareLink = z.infer<typeof ListShareLinkSchema>

export const CreateListShareLinkDtoSchema = z.object({
  allowCheckOff: z.boolean().default(true),
  allowClaim: z.boolean().default(false),
  /** Optional expiry; absent = lives until revoked. */
  expiresAt: z.coerce.date().nullable().optional(),
})
export type CreateListShareLinkDto = z.infer<typeof CreateListShareLinkDtoSchema>

/** An item claimed by a guest (stored on task.settings.externalClaim). */
export interface ExternalClaim {
  displayName: string
  at: string // ISO
}

/** Public payload — first names only, no ids beyond what actions need. */
export interface PublicSharedListItem {
  id: string
  name: string
  completed: boolean
  assigneeFirstNames: string[]
  claimedBy: string | null
}

export interface PublicSharedList {
  listName: string
  spaceName: string
  allowCheckOff: boolean
  allowClaim: boolean
  items: PublicSharedListItem[]
}

export const PublicClaimDtoSchema = z.object({
  displayName: z.string().trim().min(1).max(40),
})
export type PublicClaimDto = z.infer<typeof PublicClaimDtoSchema>

export const PublicToggleDtoSchema = z.object({
  completed: z.boolean(),
})
export type PublicToggleDto = z.infer<typeof PublicToggleDtoSchema>
